// Data-source health, recorded at the fetch sites and read by the Systems
// workspace and its rail badge.
//
// The console draws from seven upstreams, and until this module it silently
// absorbed every one of their failures: a dead NHC feed just meant no cones, a
// down Open-Meteo meant "Waiting for forecast…" forever, a stalled field cron
// meant colours that quietly stopped advancing. In fair weather that is
// tolerable; in poor weather — exactly when connectivity degrades and the
// picture matters most — a console that cannot say "this layer is stale" is a
// console lying by omission. The operator standing watch needs one glance that
// answers: which parts of this picture can I still trust?
//
// Deliberately NOT a polling loop. The console already talks to every source in
// the course of doing its job; health is those real outcomes recorded instead of
// thrown away. A poller would double the traffic precisely when the link is
// struggling, and would test reachability rather than the thing that matters —
// whether the fetches the console actually makes are succeeding.
//
// Two ages, kept distinct because they fail differently:
//   - FETCH health: is the source answering when asked? (fails counter)
//   - DATA age: how old is what it last said? (a run stamp that stops advancing
//     is the pipeline being down even while every fetch succeeds)

export type SystemId =
  | "field"
  | "forecast"
  | "ensemble"
  | "obs"
  | "tropical"
  | "jtwc"
  | "jtwcwarn"
  | "svp"
  | "argo"
  | "drifters"
  | "eez";

export type SystemStatus = "ok" | "warn" | "down" | "idle";

interface SystemMeta {
  label: string;
  /** What breaks on screen when this source does — the row's second line. */
  feeds: string;
  /** Data older than this is flagged even when fetches succeed. Null = data age
   *  is not meaningful for this source (fetched fresh per click). */
  maxDataAgeMs: number | null;
  /** A core source going down flags the rail at "stop"; the rest at "warn".
   *  Core = the two the go/no-go read itself stands on. */
  core: boolean;
}

// Order here is display order: core sources first.
//
// Field's data budget. This number has to sit ABOVE what a healthy pipeline
// reaches on its worst normal day and BELOW a missed cycle, or it is not a
// warning, it is a metronome. It was 12 h and it went off in routine operation:
//
//   cycle interval                     6 h   (00/06/12/18z)
//   NOAA posting delay               ~3.5 h  (a cycle is not there before this)
//   so the age just before the next   9.5 h
//   field-fetch runtime              ~0.6 h
//   server re-reads the row          ~0.2 h  (10 min ticker, server/field.go)
//   ------------------------------------------
//   healthy maximum                 ~10.3 h
//   ONE missed cycle                ~16.3 h
//
// 12 h left barely an hour of headroom over a figure the pipeline reaches every
// single cycle, so any NOAA slip tripped it and the board cried wolf about a
// system that was working. 14 h sits in the gap: quiet through the normal swing,
// and still unambiguous when a cycle is genuinely lost — cron down, NOAA late by
// hours, or the DB row stuck.
export const SYSTEM_META: [SystemId, SystemMeta][] = [
  ["field", { label: "Field grid", feeds: "map colours · Map·GFS-Wave trace", maxDataAgeMs: 14 * 3.6e6, core: true }],
  ["forecast", { label: "Point forecast", feeds: "charts · workability · briefing", maxDataAgeMs: null, core: true }],
  ["ensemble", { label: "GEFS ensemble", feeds: "wind spread band · workability probability", maxDataAgeMs: null, core: false }],
  ["obs", { label: "Buoy observations", feeds: "observed dots on wind/wave charts", maxDataAgeMs: 3 * 3.6e6, core: false }],
  ["tropical", { label: "NHC tropical", feeds: "cones and outlook areas on the map · storm cards", maxDataAgeMs: null, core: false }],
  // The NHC+JTWC aggregate feed that fills the storm list's non-NHC basins
  // (West Pacific, Indian Ocean, Southern Hemisphere). Down = those cards are
  // missing while the NHC ones carry on — worth its own row, not a shared one.
  ["jtwc", { label: "JTWC (Esri live feed)", feeds: "W Pacific / Indian Ocean storm cards", maxDataAgeMs: null, core: false }],
  // JTWC's own warning overlays through our relay (server/jtwc.go): the gale
  // swath, track and points drawn for those same basins. Down = the cards stay
  // (they come from the Esri feed) but the map shows no shape for them.
  ["jtwcwarn", { label: "JTWC warnings (relay)", feeds: "gale swaths, tracks and points on the map — W Pacific / Indian Ocean / S Hemisphere", maxDataAgeMs: null, core: false }],
  ["svp", { label: "Sound velocity", feeds: "SVP panel", maxDataAgeMs: null, core: false }],
  // Argo floats cycle roughly every 10 days, so a 3-week-old nearest profile is
  // entirely normal — the budget is deliberately generous. A stamp older than this
  // means the whole neighbourhood's floats have gone quiet, which is worth a flag.
  ["argo", { label: "Argo floats", feeds: "observed SVP overlay", maxDataAgeMs: 35 * 24 * 3.6e6, core: false }],
  // GDP drifters report hourly through the GTS, so a newest fix older than two days
  // means the neighbourhood's drifters have gone quiet (or the OSMC relay is stale) —
  // worth a flag even while the fetch itself succeeds. "No drifter in range" is a
  // healthy, dataAt-less ok, not a stale one: it is a fact about the ocean.
  ["drifters", { label: "Drifters (GDP)", feeds: "current vectors on map · observed dots on current track", maxDataAgeMs: 48 * 3.6e6, core: false }],
  ["eez", { label: "Jurisdiction", feeds: "EEZ readout in the header", maxDataAgeMs: null, core: false }],
];

interface SystemState {
  lastTryAt: number | null;
  lastOkAt: number | null;
  fails: number; // consecutive
  dataAt: number | null;
  note: string;
}

const state = new Map<SystemId, SystemState>();
for (const [id] of SYSTEM_META) {
  state.set(id, { lastTryAt: null, lastOkAt: null, fails: 0, dataAt: null, note: "" });
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** Record one real fetch outcome. `dataAt` is the DATA's own timestamp (model
 *  run, newest observation), not the fetch time. */
export function reportSystem(
  id: SystemId,
  ok: boolean,
  opts: { dataAt?: number | null; note?: string } = {},
): void {
  const s = state.get(id);
  if (s === undefined) return;
  const now = Date.now();
  s.lastTryAt = now;
  if (ok) {
    s.lastOkAt = now;
    s.fails = 0;
  } else {
    s.fails++;
  }
  if (opts.dataAt !== undefined) s.dataAt = opts.dataAt;
  if (opts.note !== undefined) s.note = opts.note;
  for (const fn of [...listeners]) fn();
}

export function subscribeSystems(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export interface SystemRow {
  id: SystemId;
  label: string;
  feeds: string;
  status: SystemStatus;
  /** Why the status is what it is — one clause, already worded for the row. */
  detail: string;
  core: boolean;
}

function fmtAge(ms: number): string {
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  return `${h} h ${min - h * 60} min ago`;
}

/** Derive the display rows. One fail is a blip (warn); two consecutive is down —
 *  the same one-blip tolerance the field fetch itself applies. Stale data warns
 *  even when every fetch succeeds, because that is the failure mode a fetch
 *  counter cannot see. */
export function systemsSnapshot(): SystemRow[] {
  const now = Date.now();
  return SYSTEM_META.map(([id, meta]) => {
    const s = state.get(id) as SystemState;
    let status: SystemStatus;
    let detail: string;
    if (s.lastTryAt === null) {
      status = "idle";
      detail = "not requested yet";
    } else if (s.fails >= 2) {
      status = "down";
      detail =
        `${s.fails} consecutive failures` +
        (s.lastOkAt !== null ? ` · last good ${fmtAge(now - s.lastOkAt)}` : " · never succeeded");
    } else if (s.fails === 1) {
      status = "warn";
      detail =
        "last request failed" +
        (s.lastOkAt !== null ? ` · last good ${fmtAge(now - s.lastOkAt)}` : "");
    } else if (meta.maxDataAgeMs !== null && s.dataAt !== null && now - s.dataAt > meta.maxDataAgeMs) {
      status = "warn";
      detail = `fetching fine, but the data itself is ${fmtAge(now - s.dataAt)}`;
    } else {
      status = "ok";
      detail =
        (s.dataAt !== null ? `data ${fmtAge(now - s.dataAt)}` : `checked ${fmtAge(now - (s.lastOkAt ?? now))}`) +
        (s.note !== "" ? ` · ${s.note}` : "");
    }
    return { id, label: meta.label, feeds: meta.feeds, status, detail, core: meta.core };
  });
}

/** The rail badge's one-word summary: a core source down is "stop", anything
 *  else amiss is "warn", a clean board is null. */
export function systemsBadge(): { level: "stop" | "warn"; title: string } | null {
  const rows = systemsSnapshot();
  const coreDown = rows.find((r) => r.core && r.status === "down");
  if (coreDown !== undefined) return { level: "stop", title: `${coreDown.label} is down` };
  const amiss = rows.filter((r) => r.status === "down" || r.status === "warn");
  if (amiss.length > 0) {
    return { level: "warn", title: amiss.map((r) => `${r.label}: ${r.status}`).join(" · ") };
  }
  return null;
}
